import {lazy, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {matchPath, useLocation, useParams} from "react-router";
import {css} from "@panda/css";
import lscache from "lscache";

import {Button} from "@pg-design/button-module";
import {CenterBox, Hidden, WideContent} from "@pg-design/grid-module";
import {BrandBuildingCubeIcon, BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useChangeVisibility, useIsMounted} from "@pg-mono/hooks";
import {omit} from "@pg-mono/nodash";
import {rpAppPath} from "@pg-mono/rp-routes";
import {propertyOffer} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {rpCommonUrl, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {LeadModal} from "../../application/components/LeadModal";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource} from "../../application/utils/form/application_form_source";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {Heading} from "../../atoms/Heading";
import {LocationLinks} from "../../atoms/LocationLinks";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {HotlineModal} from "../../hotline/components/HotlineModal";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {backToUrl} from "../../navigation/actions/back_to_url_actions";
import {PromotionModal} from "../../promotion/components/PromotionModal";
import {OfferPageAmplitudeItemListTrackingContextProvider} from "../../promotion/tracking/OfferPageAmplitudeItemListTrackingContext";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {offerDetailPageview} from "../../tracking/algolytics_hits/offer_detail_pageview";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {useTrackAmplitudeInvestmentViewItem} from "../../tracking/amplitude/use_track_amplitude_investment_view_item";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmInvestDetailOnOfferDetail} from "../../tracking/google_tag_manager/gtm_invest_detail";
import {useUserPreferencesQueryParams} from "../../user/hooks/use_user_preferences_query_params";
import {getUserPreferencesToOmit} from "../../user/utils/get_user_preferences_to_omit";
import {VendorDetailReportModal} from "../../vendor/components/detail/report/VendorDetailReportModal";
import {VendorReportButton} from "../../vendor/components/VendorReportButton";
import {ViewType} from "../../view_type/ViewType";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {OfferLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {GalleryHolder} from "../components/gallery_holder/GalleryHolder";
import {NearbyOffersMap} from "../components/NearbyOffersMap";
import {RelatedOfferCarouselWithModal} from "../components/offer_carousel/RelatedOfferCarouselWithModal";
import {OfferDetailHeaderHiddenPriceVariant} from "../components/offer_detail_header/OfferDetailHeaderHiddenPriceVariant";
import {OfferDetailForSale} from "../detail/components/OfferDetailForSale";
import {offerDescriptionSectionHeader, OfferDetailLimited} from "../detail/components/OfferDetailLimited";
import {OfferViewSectionTracker} from "../detail/components/OfferViewSectionTracking";
import {OfferDetailStages} from "../detail/components/stages/OfferDetailStages";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {SchemaOfferDetail} from "../detail/schema/SchemaOfferDetail";
import {SchemaOfferDetailRating} from "../detail/schema/SchemaOfferDetailRating";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {useOfferListingAmplitudePendingDetailItemListContext} from "../list/hooks/use_offer_listing_amplitude_pending_detail_item_list_context";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";
import {getUnavailableOfferInfo} from "../utils/get_unavailable_offer_info";

const HotlineChatBotModal = lazy(() => import("../../hotline/components/HotlineChatBotModal"));

export const OfferDetailView = () => {
    const dispatch = useAppDispatch();

    const offer = useAppSelector((store) => store.offerDetail.offer);
    const articles = useAppSelector((store) => store.offerDetail.articles);
    const promotions = useAppSelector((store) => store.offerDetail.promotions);
    const variables = useAppSelector((store) => store.variables?.data ?? null);
    const recommendedRegions = useAppSelector((store) => store.offerDetail.recommendedRegions);
    const relatedOffers = useAppSelector((store) => store.offerDetail.relatedOffers);
    const viewType = useAppSelector((store) => store.viewType.current);
    const isGTMInitialized = useAppSelector((store) => store.googleTagManager);
    const {offerId: routeOfferIdParam} = useParams<{offerId: string}>();
    const routeOfferId = Number(routeOfferIdParam);
    const isCurrentRouteOffer = offer !== null && (offer.id === routeOfferId || offer.group_primary_offer?.id === routeOfferId);
    const {itemListContext: offerListingAmplitudeItemListContext, isResolved: isOfferListingAmplitudeItemListContextResolved} =
        useOfferListingAmplitudePendingDetailItemListContext({
            itemId: isCurrentRouteOffer ? offer.id : undefined
        });

    useTrackAmplitudeInvestmentViewItem({
        offer,
        itemListContext: offerListingAmplitudeItemListContext,
        isCurrentRouteOffer,
        isItemListContextResolved: isOfferListingAmplitudeItemListContextResolved
    });

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {shouldHidePrices} = useShouldHidePrices();

    const userPreferencesQueryParams = useUserPreferencesQueryParams();

    const getFriendlyOfferListLink = () => {
        const isFlat = offer?.type === OfferType.FLAT;
        const stats = offer?.region?.stats;

        const isEmpty = isFlat ? (stats?.properties_flats_count_for_sale ?? 0) <= 0 : (stats?.properties_houses_count_for_sale ?? 0) <= 0;

        const regionSlug = isEmpty ? offer?.region?.parent?.slug : offer?.region?.slug;
        const country = offer?.region?.country;
        const type = isFlat ? OfferType.FLAT : OfferType.HOUSE;

        const clearedPreferences = omit(userPreferencesQueryParams, getUserPreferencesToOmit(offer?.type));

        return buildFriendlyOfferListLink({type, region: regionSlug, country: country === Country.POLAND ? undefined : country, ...clearedPreferences});
    };

    const regionName =
        offer !== null &&
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.name
                : offer.region.name
            : offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.name
              : offer.region.name);

    useEffect(() => {
        if (offer) {
            offerDetailPageview(offer, offer.vendor);
        }

        const prevUrl = document.referrer;

        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                dispatch(backToUrl(prevUrl, "OFERTY"));
                return;
            }
        }

        dispatch(backToUrl(getFriendlyOfferListLink(), `oferty ${regionName}`));
    }, [offer, userPreferencesQueryParams, dispatch]);

    /*
    This particular event has to be fired only once. Event checks if gtm is initialized and offer data is available.
    Because of SSR offer is already available and dependency in useEffect is set to GTM flag from store.
    This is crucial for the event to fire in correct order.
     */
    useEffect(() => {
        if (offer && isGTMInitialized) {
            gtmInvestDetailOnOfferDetail(offer);
        }
    }, [isGTMInitialized]);

    useHitUserSegmentLocationEntryOnce({
        enabled: !!offer,
        regions: offer ? [offer.region] : []
    });

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection | null) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType, source ?? null);
        }
        _setIsModalAndTabActive(tab);
    };

    const onShowMapClick = () => {
        if (offer) {
            trackAmplitudeButtonClick({buttonName: "view_map", value: "see_on_map", buttonCategory: "investment_page", itemId: offer.id});
        }
        setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.BUTTON);
    };

    const onShowConstructionGalleryClick = () => {
        if (offer) {
            trackAmplitudeButtonClick({
                buttonName: "view_construction_photos",
                value: "see_construction_photos",
                buttonCategory: "investment_page",
                itemId: offer.id
            });
        }
        setIsModalAndTabActive(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BUTTON);
    };

    const isAbroad = offer?.region.country !== Country.POLAND;
    const backToPaths = [rpAppPath.friendlyOfferList, rpAppPath.multiOfferList.base, rpAppPath.offer.list];
    const isForSale = offer?.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isPlanned = offer?.configuration.display_type === OfferDisplayType.PLANNED;
    const isSold = offer?.configuration.display_type === OfferDisplayType.SOLD;
    const isLimited = offer && getOfferDisplayStatus(offer) === "limited";
    const {isArchived, isUnavailable} = getUnavailableOfferInfo(offer);
    const isClassicView = isForSale && !isLimited;
    const regionStatsForSale =
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.stats && offer.region.parent.stats.properties_flats_count_for_sale
                : offer.region.stats?.properties_flats_count_for_sale
            : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.stats && offer.region.parent.stats.properties_houses_count_for_sale
              : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale) || false;

    const refGallery = useRef<HTMLTableSectionElement>(null);
    const refProperties = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refDescription = useRef<HTMLTableSectionElement>(null);
    const refLocation = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(
        () => ({
            refGallery,
            refProperties,
            refDescription,
            refPromotions,
            refLocation,
            refContact
        }),
        []
    );

    const hasProperties = offer != null ? (offer.stats?.properties_count_for_sale ?? 0) > 0 : false;
    const hasPromotions = promotions.length > 0;
    const hasDescription = offer != null ? !!(offer.description || offer.configuration.show_generated_description) : false;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const location = useLocation();

    const currentStageOfferCount = offer?.groups?.stages.filter((stage) => stage.current);
    const hasVisibleStages = offer?.groups?.stages.some((stage) => !stage.hidden) ?? false;

    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    const changeModalTab = useCallback(
        (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection): void => {
            setIsModalAndTabActive(tab, source);

            offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
        },
        [offer, fetchOfferBuildings]
    );

    // Scroll and visibility solution
    const getElems = useCallback(() => {
        const res = [];
        if (hasProperties) {
            res.push("properties");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasDescription) {
            res.push("description");
        }
        res.push("location");
        res.push("contact");
        return res;
    }, [hasProperties, hasPromotions, hasDescription]);

    const [visibleElem, changeVisibility] = useChangeVisibility(getElems, 100);

    /**
     * Render
     */

    if (offer == null) {
        return (
            <CenterBox height="500px">
                <Loader size="lg" />
            </CenterBox>
        );
    }
    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        offer.region.stats || {};

    const breadcrumbs = {
        region_type_voivodeship: region_type_voivodeship || null,
        region_type_city: region_type_city || null,
        region_type_county: region_type_county || null,
        region_type_district: region_type_district || null,
        region_type_housing_estate: region_type_housing_estate || null,
        region_type_town: region_type_town || null
    };
    const {vendor} = offer;

    const newHeaderVariant = shouldHidePrices ? "blur" : "visible";

    const shouldRenderModal = !isArchived && !!offer;
    const hasActivePromotions = !!offer && (offer.has_active_promotions || promotions.length > 0);
    const hotlineWidgetTrackingEventCategory = region_type_city?.name || region_type_town?.name || offer.region.name;

    return (
        <OfferPageAmplitudeItemListTrackingContextProvider
            itemListContext={offerListingAmplitudeItemListContext}
            isResolved={isOfferListingAmplitudeItemListContextResolved}
            pageLocation="investment_page"
        >
            <article>
                <section data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_BREADCRUMBS_SECTION}>
                    <WideContent className={css({mt: 2})}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                        />
                    </WideContent>
                </section>

                <WideContent className={headerWrapper}>
                    <OfferDetailHeaderHiddenPriceVariant
                        offer={offer}
                        variant={newHeaderVariant}
                        promotions={promotions}
                        onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.HEADER)}
                    />
                </WideContent>

                {isPlanned && !isArchived && (
                    <WideContent>
                        <div className={mainMessageWrap}>
                            <Text className={mainMessageTitleStyle}>Inwestycja planowana</Text>

                            <Text variant="body_copy_2">
                                Uwaga! Deweloper nie rozpoczął jeszcze sprzedaży mieszkań w tej inwestycji. {regionStatsForSale && `Mamy dla Ciebie`}{" "}
                                {regionStatsForSale && (
                                    <a className={noWrapUnderlinedStyle} href={getFriendlyOfferListLink()}>
                                        {regionStatsForSale} {propertyOffer.accusative(regionStatsForSale)}
                                    </a>
                                )}{" "}
                                {regionStatsForSale && `w\xa0najbliższej okolicy!`}
                            </Text>

                            {isSold && (
                                <>
                                    <InactiveInfo />
                                    <NearestOffersInfo regionStatsForSale={regionStatsForSale} friendlyOfferListLink={getFriendlyOfferListLink()} />
                                </>
                            )}
                        </div>
                    </WideContent>
                )}

                {(isArchived || isUnavailable) && !isPlanned && (
                    <>
                        <WideContent>
                            <div className={mainMessageWrap}>
                                <Text className={mainMessageTitleStyle}>{isArchived ? "Oferta archiwalna" : "Oferta nieaktywna"}</Text>

                                {(isSold || isLimited) && (
                                    <>
                                        <InactiveInfo isUnavailable={isUnavailable} />
                                        <NearestOffersInfo
                                            regionStatsForSale={regionStatsForSale}
                                            friendlyOfferListLink={
                                                currentStageOfferCount &&
                                                currentStageOfferCount.length > 0 &&
                                                currentStageOfferCount[0].offer.stats.properties_count_for_sale > 0
                                                    ? location.pathname
                                                    : getFriendlyOfferListLink()
                                            }
                                            currentStageOfferCount={
                                                currentStageOfferCount && currentStageOfferCount.length > 0
                                                    ? currentStageOfferCount[0].offer.stats.properties_count_for_sale
                                                    : null
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </WideContent>

                        {hasVisibleStages && <OfferViewSectionTracker itemId={offer.id} sectionName="stages" />}
                        <OfferDetailStages offer={offer} isArchived />
                    </>
                )}

                {!isClassicView && (
                    <WideContent>
                        {relatedOffers.length > 0 && (
                            <>
                                <OfferViewSectionTracker itemId={offer.id} sectionName="recommended_offers" />
                                <Heading type="h2" className={offerDescriptionSectionHeader}>
                                    {isArchived || isUnavailable ? "Podobne oferty w najbliższej okolicy" : "Najbliższe inwestycje"}
                                </Heading>

                                {(isArchived || isUnavailable) && (
                                    <RelatedOfferCarouselWithModal
                                        offerIds={relatedOffers.map((offer) => offer.id)}
                                        trackingMeta={
                                            isArchived || isUnavailable
                                                ? {
                                                      listName: `Podobne oferty ${isArchived ? "archiwalne" : "nieaktywne"} w najbliższej okolicy`,
                                                      listId: `podobne_oferty_${isArchived ? "archiwalne" : "nieaktywne"}_w_najblizszej_okolicy`,
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                                : {
                                                      listName: "Najbliższe inwestycje",
                                                      listId: "najblizsze_inwestycje",
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                        }
                                    />
                                )}

                                <div className={mapWrapper}>
                                    <NearbyOffersMap
                                        viewType={ViewType.OFFER}
                                        originCountry={offer.region.country}
                                        originCoordinates={offer.geo_point.coordinates}
                                        offers={relatedOffers}
                                    />
                                </div>
                            </>
                        )}

                        {(isSold || (isLimited && !isPlanned)) && (
                            <Heading type="h2" className={offerDescriptionSectionHeader}>
                                {isUnavailable ? "Prezentacja oferty nieaktywnej" : "Prezentacja oferty archiwalnej"}
                            </Heading>
                        )}
                    </WideContent>
                )}

                <section id="galeria" ref={refElems.refGallery} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_GALLERY_SECTION}>
                    <OfferViewSectionTracker itemId={offer.id} sectionName="gallery" />
                    <GalleryHolder
                        source={ApplicationSource.Offer}
                        offer={offer}
                        offerBuildings={offerBuildings}
                        isOpen={modalIsOpen}
                        handleGalleryModalChange={(tab) => setIsModalAndTabActive(tab, GTMEventApplicationSourceSection.IMAGE)}
                        modalState={isModalAndTabActive}
                        onModalClose={() => {
                            setModalIsOpen(false);
                        }}
                    />
                </section>

                <Hidden on="desktop">
                    <WideContent>
                        <div className={mobileIconButtonsWrapper} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_ACTIONS_SECTION_MOBILE}>
                            {rpFeatureFlagVendorReportDownload && !isAbroad && isMobile && <VendorReportButton vendorId={offer.vendor.id} offerId={offer.id} />}

                            <Button
                                size="medium"
                                variant="brand_icon"
                                iconLeft={BrandMapPinIcon}
                                onClick={onShowMapClick}
                                className={css({m: 0, p: 0})}
                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                            >
                                Zobacz na mapie
                            </Button>

                            <DetailConstructionGalleryCTA onButtonClick={onShowConstructionGalleryClick} />

                            <Planner3dModal offer={offer} className={planner3DStyle} routedModalId="view">
                                <Button
                                    iconLeft={BrandBuildingCubeIcon}
                                    variant="brand_icon"
                                    size={isMobile ? "medium" : "big"}
                                    className={css({mb: 2, pl: 0})}
                                >
                                    Plan pięter
                                </Button>
                            </Planner3dModal>
                        </div>
                    </WideContent>
                </Hidden>

                {isClassicView ? (
                    <OfferDetailForSale
                        offer={offer}
                        promotions={promotions}
                        variables={variables}
                        refElems={refElems}
                        visibleElement={visibleElem}
                        changeVisibility={changeVisibility}
                        changeModalTab={changeModalTab}
                        hasProperties={hasProperties}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                ) : (
                    <OfferDetailLimited
                        offer={offer}
                        refElems={refElems}
                        variables={variables}
                        changeModalTab={changeModalTab}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                )}

                {articles.length > 0 && (
                    <>
                        <OfferViewSectionTracker itemId={offer.id} sectionName="related_articles" />
                        <RelatedArticlesSection heading="Artykuły powiązane:" articles={articles} />
                    </>
                )}

                <SchemaOfferDetail offer={offer} />

                {!isSold && offer.is_active && <SchemaOfferDetailRating offer={offer} />}

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {offer && offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper className={linksWrapperStyle}>
                        <LocationLinks recommendedRegions={recommendedRegions} offerType={offer.type} region={offer.region} relatedOffers={relatedOffers} />
                    </OfferLocationLinksWrapper>
                )}
            </article>

            {/** View Modals */}
            <FavouritesInfoModal offerId={offer?.id} />

            <LeadModal />

            <ForYouModal />

            {isMounted && (
                <HotlineChatBotModal hotlineModalTriggerId={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} trackingEventCategory={hotlineWidgetTrackingEventCategory} />
            )}

            {shouldRenderModal &&
                (hasActivePromotions ? (
                    <PromotionModal vendorId={offer.vendor.id} offerId={offer.id} propertyId={null} promotion={promotions[0]} fallbackTitle={offer.name} />
                ) : (
                    <HotlineModal trackingEventCategory={hotlineWidgetTrackingEventCategory} />
                ))}

            {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorDetailReportModal />}
        </OfferPageAmplitudeItemListTrackingContextProvider>
    );
};

// Sub components
interface INearestOffersInfoProps {
    friendlyOfferListLink: string;
    regionStatsForSale?: number | false;
    currentStageOfferCount?: number | null;
}

function NearestOffersInfo(props: INearestOffersInfoProps) {
    const {regionStatsForSale, friendlyOfferListLink, currentStageOfferCount} = props;

    const saleStats = currentStageOfferCount ? currentStageOfferCount : regionStatsForSale;

    if (!saleStats) {
        return;
    }

    return (
        <Text variant="body_copy_2" className={css({pt: 2})}>
            {`Mamy dla Ciebie `}
            <a className={noWrapUnderlinedStyle} href={friendlyOfferListLink}>
                {saleStats} {propertyOffer.accusative(saleStats)}
            </a>

            {currentStageOfferCount ? ` w kolejnym etapie tej inwestycji!` : " w najbliższej okolicy!"}
        </Text>
    );
}

function InactiveInfo({isUnavailable}: {isUnavailable?: boolean}) {
    return (
        <Text variant="body_copy_2">
            {isUnavailable
                ? "Uwaga! Wizytówka niniejszej inwestycji jest nieaktywna." //Zapisz się, żeby dostać powiadomienie, gdy inwestycja trafi do sprzedaży."
                : "Uwaga! Niniejsza inwestycja została wyprzedana. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat lokalnego rynku nieruchomości."}
        </Text>
    );
}

//  Styles

const headerWrapper = css({
    my: 2,

    lg: {
        mt: 3,
        mb: 2
    }
});

const linksWrapperStyle = css({
    p: 0,
    backgroundColor: "var(--colors-white)",

    md: {
        py: "6.4rem !important"
    }
});

const mainMessageWrap = css({
    mb: 2,
    borderRadius: 2,
    p: 2,
    background: "var(--colors-white)",
    boxShadow: "var(--shadow.0)"
});

const mainMessageTitleStyle = css({
    mb: 2
});

const noWrapUnderlinedStyle = css({
    textWrap: "unset",
    textDecoration: "underline"
});

const mapWrapper = css({
    mb: 3,
    position: "relative",
    width: "100%",
    height: "500px",

    sm: {
        height: "600px"
    }
});

const planner3DStyle = css({
    display: "inline-block"
});

const mobileIconButtonsWrapper = css({
    display: "flex",
    flexWrap: "wrap",
    columnGap: "2.7rem",
    rowGap: 2,
    mb: 2
});
import {lazy, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {matchPath, useLocation, useParams} from "react-router";
import {css} from "@panda/css";
import lscache from "lscache";

import {Button} from "@pg-design/button-module";
import {CenterBox, Hidden, WideContent} from "@pg-design/grid-module";
import {BrandBuildingCubeIcon, BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useChangeVisibility, useIsMounted} from "@pg-mono/hooks";
import {omit} from "@pg-mono/nodash";
import {rpAppPath} from "@pg-mono/rp-routes";
import {propertyOffer} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {rpCommonUrl, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {LeadModal} from "../../application/components/LeadModal";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource} from "../../application/utils/form/application_form_source";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {Heading} from "../../atoms/Heading";
import {LocationLinks} from "../../atoms/LocationLinks";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {useHitUserSegmentLocationEntryOnce} from "../../hooks/use_hit_user_segment_location_entry_once";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {HotlineModal} from "../../hotline/components/HotlineModal";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {backToUrl} from "../../navigation/actions/back_to_url_actions";
import {PromotionModal} from "../../promotion/components/PromotionModal";
import {OfferPageAmplitudeItemListTrackingContextProvider} from "../../promotion/tracking/OfferPageAmplitudeItemListTrackingContext";
import {Country} from "../../region/types/Country";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {offerDetailPageview} from "../../tracking/algolytics_hits/offer_detail_pageview";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {useTrackAmplitudeInvestmentViewItem} from "../../tracking/amplitude/use_track_amplitude_investment_view_item";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmInvestDetailOnOfferDetail} from "../../tracking/google_tag_manager/gtm_invest_detail";
import {useUserPreferencesQueryParams} from "../../user/hooks/use_user_preferences_query_params";
import {getUserPreferencesToOmit} from "../../user/utils/get_user_preferences_to_omit";
import {VendorDetailReportModal} from "../../vendor/components/detail/report/VendorDetailReportModal";
import {VendorReportButton} from "../../vendor/components/VendorReportButton";
import {ViewType} from "../../view_type/ViewType";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {OfferLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {GalleryHolder} from "../components/gallery_holder/GalleryHolder";
import {NearbyOffersMap} from "../components/NearbyOffersMap";
import {RelatedOfferCarouselWithModal} from "../components/offer_carousel/RelatedOfferCarouselWithModal";
import {OfferDetailHeaderHiddenPriceVariant} from "../components/offer_detail_header/OfferDetailHeaderHiddenPriceVariant";
import {OfferDetailForSale} from "../detail/components/OfferDetailForSale";
import {offerDescriptionSectionHeader, OfferDetailLimited} from "../detail/components/OfferDetailLimited";
import {OfferViewSectionTracker} from "../detail/components/OfferViewSectionTracking";
import {OfferDetailStages} from "../detail/components/stages/OfferDetailStages";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {SchemaOfferDetail} from "../detail/schema/SchemaOfferDetail";
import {SchemaOfferDetailRating} from "../detail/schema/SchemaOfferDetailRating";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {useOfferListingAmplitudePendingDetailItemListContext} from "../list/hooks/use_offer_listing_amplitude_pending_detail_item_list_context";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";
import {getUnavailableOfferInfo} from "../utils/get_unavailable_offer_info";

const HotlineChatBotModal = lazy(() => import("../../hotline/components/HotlineChatBotModal"));

export const OfferDetailView = () => {
    const dispatch = useAppDispatch();

    const offer = useAppSelector((store) => store.offerDetail.offer);
    const articles = useAppSelector((store) => store.offerDetail.articles);
    const promotions = useAppSelector((store) => store.offerDetail.promotions);
    const variables = useAppSelector((store) => store.variables?.data ?? null);
    const recommendedRegions = useAppSelector((store) => store.offerDetail.recommendedRegions);
    const relatedOffers = useAppSelector((store) => store.offerDetail.relatedOffers);
    const viewType = useAppSelector((store) => store.viewType.current);
    const isGTMInitialized = useAppSelector((store) => store.googleTagManager);
    const {offerId: routeOfferIdParam} = useParams<{offerId: string}>();
    const routeOfferId = Number(routeOfferIdParam);
    const isCurrentRouteOffer = offer !== null && (offer.id === routeOfferId || offer.group_primary_offer?.id === routeOfferId);
    const {itemListContext: offerListingAmplitudeItemListContext, isResolved: isOfferListingAmplitudeItemListContextResolved} =
        useOfferListingAmplitudePendingDetailItemListContext({
            itemId: isCurrentRouteOffer ? offer.id : undefined
        });

    useTrackAmplitudeInvestmentViewItem({
        offer,
        itemListContext: offerListingAmplitudeItemListContext,
        isCurrentRouteOffer,
        isItemListContextResolved: isOfferListingAmplitudeItemListContextResolved
    });

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {shouldHidePrices} = useShouldHidePrices();

    const userPreferencesQueryParams = useUserPreferencesQueryParams();

    const getFriendlyOfferListLink = () => {
        const isFlat = offer?.type === OfferType.FLAT;
        const stats = offer?.region?.stats;

        const isEmpty = isFlat ? (stats?.properties_flats_count_for_sale ?? 0) <= 0 : (stats?.properties_houses_count_for_sale ?? 0) <= 0;

        const regionSlug = isEmpty ? offer?.region?.parent?.slug : offer?.region?.slug;
        const country = offer?.region?.country;
        const type = isFlat ? OfferType.FLAT : OfferType.HOUSE;

        const clearedPreferences = omit(userPreferencesQueryParams, getUserPreferencesToOmit(offer?.type));

        return buildFriendlyOfferListLink({type, region: regionSlug, country: country === Country.POLAND ? undefined : country, ...clearedPreferences});
    };

    const regionName =
        offer !== null &&
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.name
                : offer.region.name
            : offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.name
              : offer.region.name);

    useEffect(() => {
        if (offer) {
            offerDetailPageview(offer, offer.vendor);
        }

        const prevUrl = document.referrer;

        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                dispatch(backToUrl(prevUrl, "OFERTY"));
                return;
            }
        }

        dispatch(backToUrl(getFriendlyOfferListLink(), `oferty ${regionName}`));
    }, [offer, userPreferencesQueryParams, dispatch]);

    /*
    This particular event has to be fired only once. Event checks if gtm is initialized and offer data is available.
    Because of SSR offer is already available and dependency in useEffect is set to GTM flag from store.
    This is crucial for the event to fire in correct order.
     */
    useEffect(() => {
        if (offer && isGTMInitialized) {
            gtmInvestDetailOnOfferDetail(offer);
        }
    }, [isGTMInitialized]);

    useHitUserSegmentLocationEntryOnce({
        enabled: !!offer,
        regions: offer ? [offer.region] : []
    });

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection | null) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType, source ?? null);
        }
        _setIsModalAndTabActive(tab);
    };

    const onShowMapClick = () => {
        if (offer) {
            trackAmplitudeButtonClick({buttonName: "view_map", value: "see_on_map", buttonCategory: "investment_page", itemId: offer.id});
        }
        setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.BUTTON);
    };

    const onShowConstructionGalleryClick = () => {
        if (offer) {
            trackAmplitudeButtonClick({
                buttonName: "view_construction_photos",
                value: "see_construction_photos",
                buttonCategory: "investment_page",
                itemId: offer.id
            });
        }
        setIsModalAndTabActive(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BUTTON);
    };

    const isAbroad = offer?.region.country !== Country.POLAND;
    const backToPaths = [rpAppPath.friendlyOfferList, rpAppPath.multiOfferList.base, rpAppPath.offer.list];
    const isForSale = offer?.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isPlanned = offer?.configuration.display_type === OfferDisplayType.PLANNED;
    const isSold = offer?.configuration.display_type === OfferDisplayType.SOLD;
    const isLimited = offer && getOfferDisplayStatus(offer) === "limited";
    const {isArchived, isUnavailable} = getUnavailableOfferInfo(offer);
    const isClassicView = isForSale && !isLimited;
    const regionStatsForSale =
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.stats && offer.region.parent.stats.properties_flats_count_for_sale
                : offer.region.stats?.properties_flats_count_for_sale
            : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.stats && offer.region.parent.stats.properties_houses_count_for_sale
              : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale) || false;

    const refGallery = useRef<HTMLTableSectionElement>(null);
    const refProperties = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refDescription = useRef<HTMLTableSectionElement>(null);
    const refLocation = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(
        () => ({
            refGallery,
            refProperties,
            refDescription,
            refPromotions,
            refLocation,
            refContact
        }),
        []
    );

    const hasProperties = offer != null ? (offer.stats?.properties_count_for_sale ?? 0) > 0 : false;
    const hasPromotions = promotions.length > 0;
    const hasDescription = offer != null ? !!(offer.description || offer.configuration.show_generated_description) : false;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const location = useLocation();

    const currentStageOfferCount = offer?.groups?.stages.filter((stage) => stage.current);
    const hasVisibleStages = offer?.groups?.stages.some((stage) => !stage.hidden) ?? false;

    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    const changeModalTab = useCallback(
        (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection): void => {
            setIsModalAndTabActive(tab, source);

            offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
        },
        [offer, fetchOfferBuildings]
    );

    // Scroll and visibility solution
    const getElems = useCallback(() => {
        const res = [];
        if (hasProperties) {
            res.push("properties");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasDescription) {
            res.push("description");
        }
        res.push("location");
        res.push("contact");
        return res;
    }, [hasProperties, hasPromotions, hasDescription]);

    const [visibleElem, changeVisibility] = useChangeVisibility(getElems, 100);

    /**
     * Render
     */

    if (offer == null) {
        return (
            <CenterBox height="500px">
                <Loader size="lg" />
            </CenterBox>
        );
    }
    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        offer.region.stats || {};

    const breadcrumbs = {
        region_type_voivodeship: region_type_voivodeship || null,
        region_type_city: region_type_city || null,
        region_type_county: region_type_county || null,
        region_type_district: region_type_district || null,
        region_type_housing_estate: region_type_housing_estate || null,
        region_type_town: region_type_town || null
    };
    const {vendor} = offer;

    const newHeaderVariant = shouldHidePrices ? "blur" : "visible";

    const shouldRenderModal = !isArchived && !!offer;
    const hasActivePromotions = !!offer && (offer.has_active_promotions || promotions.length > 0);
    const hotlineWidgetTrackingEventCategory = region_type_city?.name || region_type_town?.name || offer.region.name;

    return (
        <OfferPageAmplitudeItemListTrackingContextProvider
            itemListContext={offerListingAmplitudeItemListContext}
            isResolved={isOfferListingAmplitudeItemListContextResolved}
            pageLocation="investment_page"
        >
            <article>
                <section data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_BREADCRUMBS_SECTION}>
                    <WideContent className={css({mt: 2})}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                        />
                    </WideContent>
                </section>

                <WideContent className={headerWrapper}>
                    <OfferDetailHeaderHiddenPriceVariant
                        offer={offer}
                        variant={newHeaderVariant}
                        promotions={promotions}
                        onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.HEADER)}
                    />
                </WideContent>

                {isPlanned && !isArchived && (
                    <WideContent>
                        <div className={mainMessageWrap}>
                            <Text className={mainMessageTitleStyle}>Inwestycja planowana</Text>

                            <Text variant="body_copy_2">
                                Uwaga! Deweloper nie rozpoczął jeszcze sprzedaży mieszkań w tej inwestycji. {regionStatsForSale && `Mamy dla Ciebie`}{" "}
                                {regionStatsForSale && (
                                    <a className={noWrapUnderlinedStyle} href={getFriendlyOfferListLink()}>
                                        {regionStatsForSale} {propertyOffer.accusative(regionStatsForSale)}
                                    </a>
                                )}{" "}
                                {regionStatsForSale && `w\xa0najbliższej okolicy!`}
                            </Text>

                            {isSold && (
                                <>
                                    <InactiveInfo />
                                    <NearestOffersInfo regionStatsForSale={regionStatsForSale} friendlyOfferListLink={getFriendlyOfferListLink()} />
                                </>
                            )}
                        </div>
                    </WideContent>
                )}

                {(isArchived || isUnavailable) && !isPlanned && (
                    <>
                        <WideContent>
                            <div className={mainMessageWrap}>
                                <Text className={mainMessageTitleStyle}>{isArchived ? "Oferta archiwalna" : "Oferta nieaktywna"}</Text>

                                {(isSold || isLimited) && (
                                    <>
                                        <InactiveInfo isUnavailable={isUnavailable} />
                                        <NearestOffersInfo
                                            regionStatsForSale={regionStatsForSale}
                                            friendlyOfferListLink={
                                                currentStageOfferCount &&
                                                currentStageOfferCount.length > 0 &&
                                                currentStageOfferCount[0].offer.stats.properties_count_for_sale > 0
                                                    ? location.pathname
                                                    : getFriendlyOfferListLink()
                                            }
                                            currentStageOfferCount={
                                                currentStageOfferCount && currentStageOfferCount.length > 0
                                                    ? currentStageOfferCount[0].offer.stats.properties_count_for_sale
                                                    : null
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </WideContent>

                        {hasVisibleStages && <OfferViewSectionTracker itemId={offer.id} sectionName="stages" />}
                        <OfferDetailStages offer={offer} isArchived />
                    </>
                )}

                {!isClassicView && (
                    <WideContent>
                        {relatedOffers.length > 0 && (
                            <>
                                <OfferViewSectionTracker itemId={offer.id} sectionName="recommended_offers" />
                                <Heading type="h2" className={offerDescriptionSectionHeader}>
                                    {isArchived || isUnavailable ? "Podobne oferty w najbliższej okolicy" : "Najbliższe inwestycje"}
                                </Heading>

                                {(isArchived || isUnavailable) && (
                                    <RelatedOfferCarouselWithModal
                                        offerIds={relatedOffers.map((offer) => offer.id)}
                                        trackingMeta={
                                            isArchived || isUnavailable
                                                ? {
                                                      listName: `Podobne oferty ${isArchived ? "archiwalne" : "nieaktywne"} w najbliższej okolicy`,
                                                      listId: `podobne_oferty_${isArchived ? "archiwalne" : "nieaktywne"}_w_najblizszej_okolicy`,
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                                : {
                                                      listName: "Najbliższe inwestycje",
                                                      listId: "najblizsze_inwestycje",
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                        }
                                    />
                                )}

                                <div className={mapWrapper}>
                                    <NearbyOffersMap
                                        viewType={ViewType.OFFER}
                                        originCountry={offer.region.country}
                                        originCoordinates={offer.geo_point.coordinates}
                                        offers={relatedOffers}
                                    />
                                </div>
                            </>
                        )}

                        {(isSold || (isLimited && !isPlanned)) && (
                            <Heading type="h2" className={offerDescriptionSectionHeader}>
                                {isUnavailable ? "Prezentacja oferty nieaktywnej" : "Prezentacja oferty archiwalnej"}
                            </Heading>
                        )}
                    </WideContent>
                )}

                <section id="galeria" ref={refElems.refGallery} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_GALLERY_SECTION}>
                    <OfferViewSectionTracker itemId={offer.id} sectionName="gallery" />
                    <GalleryHolder
                        source={ApplicationSource.Offer}
                        offer={offer}
                        offerBuildings={offerBuildings}
                        isOpen={modalIsOpen}
                        handleGalleryModalChange={(tab) => setIsModalAndTabActive(tab, GTMEventApplicationSourceSection.IMAGE)}
                        modalState={isModalAndTabActive}
                        onModalClose={() => {
                            setModalIsOpen(false);
                        }}
                    />
                </section>

                <Hidden on="desktop">
                    <WideContent>
                        <div className={mobileIconButtonsWrapper} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_ACTIONS_SECTION_MOBILE}>
                            {rpFeatureFlagVendorReportDownload && !isAbroad && isMobile && <VendorReportButton vendorId={offer.vendor.id} offerId={offer.id} />}

                            <Button
                                size="medium"
                                variant="brand_icon"
                                iconLeft={BrandMapPinIcon}
                                onClick={onShowMapClick}
                                className={css({m: 0, p: 0})}
                                dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                            >
                                Zobacz na mapie
                            </Button>

                            <DetailConstructionGalleryCTA onButtonClick={onShowConstructionGalleryClick} />

                            <Planner3dModal offer={offer} className={planner3DStyle} routedModalId="view">
                                <Button
                                    iconLeft={BrandBuildingCubeIcon}
                                    variant="brand_icon"
                                    size={isMobile ? "medium" : "big"}
                                    className={css({mb: 2, pl: 0})}
                                >
                                    Plan pięter
                                </Button>
                            </Planner3dModal>
                        </div>
                    </WideContent>
                </Hidden>

                {isClassicView ? (
                    <OfferDetailForSale
                        offer={offer}
                        promotions={promotions}
                        variables={variables}
                        refElems={refElems}
                        visibleElement={visibleElem}
                        changeVisibility={changeVisibility}
                        changeModalTab={changeModalTab}
                        hasProperties={hasProperties}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                ) : (
                    <OfferDetailLimited
                        offer={offer}
                        refElems={refElems}
                        variables={variables}
                        changeModalTab={changeModalTab}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                )}

                {articles.length > 0 && (
                    <>
                        <OfferViewSectionTracker itemId={offer.id} sectionName="related_articles" />
                        <RelatedArticlesSection heading="Artykuły powiązane:" articles={articles} />
                    </>
                )}

                <SchemaOfferDetail offer={offer} />

                {!isSold && offer.is_active && <SchemaOfferDetailRating offer={offer} />}

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {offer && offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper className={linksWrapperStyle}>
                        <LocationLinks recommendedRegions={recommendedRegions} offerType={offer.type} region={offer.region} relatedOffers={relatedOffers} />
                    </OfferLocationLinksWrapper>
                )}
            </article>

            {/** View Modals */}
            <FavouritesInfoModal offerId={offer?.id} />

            <LeadModal />

            <ForYouModal />

            {isMounted && (
                <HotlineChatBotModal hotlineModalTriggerId={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} trackingEventCategory={hotlineWidgetTrackingEventCategory} />
            )}

            {shouldRenderModal &&
                (hasActivePromotions ? (
                    <PromotionModal vendorId={offer.vendor.id} offerId={offer.id} propertyId={null} promotion={promotions[0]} fallbackTitle={offer.name} />
                ) : (
                    <HotlineModal trackingEventCategory={hotlineWidgetTrackingEventCategory} />
                ))}

            {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorDetailReportModal />}
        </OfferPageAmplitudeItemListTrackingContextProvider>
    );
};

// Sub components
interface INearestOffersInfoProps {
    friendlyOfferListLink: string;
    regionStatsForSale?: number | false;
    currentStageOfferCount?: number | null;
}

function NearestOffersInfo(props: INearestOffersInfoProps) {
    const {regionStatsForSale, friendlyOfferListLink, currentStageOfferCount} = props;

    const saleStats = currentStageOfferCount ? currentStageOfferCount : regionStatsForSale;

    if (!saleStats) {
        return;
    }

    return (
        <Text variant="body_copy_2" className={css({pt: 2})}>
            {`Mamy dla Ciebie `}
            <a className={noWrapUnderlinedStyle} href={friendlyOfferListLink}>
                {saleStats} {propertyOffer.accusative(saleStats)}
            </a>

            {currentStageOfferCount ? ` w kolejnym etapie tej inwestycji!` : " w najbliższej okolicy!"}
        </Text>
    );
}

function InactiveInfo({isUnavailable}: {isUnavailable?: boolean}) {
    return (
        <Text variant="body_copy_2">
            {isUnavailable
                ? "Uwaga! Wizytówka niniejszej inwestycji jest nieaktywna." //Zapisz się, żeby dostać powiadomienie, gdy inwestycja trafi do sprzedaży."
                : "Uwaga! Niniejsza inwestycja została wyprzedana. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat lokalnego rynku nieruchomości."}
        </Text>
    );
}

//  Styles

const headerWrapper = css({
    my: 2,

    lg: {
        mt: 3,
        mb: 2
    }
});

const linksWrapperStyle = css({
    p: 0,
    backgroundColor: "var(--colors-white)",

    md: {
        py: "6.4rem !important"
    }
});

const mainMessageWrap = css({
    mb: 2,
    borderRadius: 2,
    p: 2,
    background: "var(--colors-white)",
    boxShadow: "var(--shadow.0)"
});

const mainMessageTitleStyle = css({
    mb: 2
});

const noWrapUnderlinedStyle = css({
    textWrap: "unset",
    textDecoration: "underline"
});

const mapWrapper = css({
    mb: 3,
    position: "relative",
    width: "100%",
    height: "500px",

    sm: {
        height: "600px"
    }
});

const planner3DStyle = css({
    display: "inline-block"
});

const mobileIconButtonsWrapper = css({
    display: "flex",
    flexWrap: "wrap",
    columnGap: "2.7rem",
    rowGap: 2,
    mb: 2
});
